import { DialogComponent } from '@theme/dialog';

const STORAGE_KEY = 'zest-delites:experience';

/**
 * Splash modal that asks visitors to choose between the Zest Delites (snacks)
 * and Zest Delites+ (energy gels) experiences. Opens on the first page load,
 * persists the choice in localStorage (cookie fallback) so visitors are not
 * re-prompted.
 *
 * @extends DialogComponent
 */
class SplashModalComponent extends DialogComponent {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #openTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('dialog:close', this.#handleClose);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);
      return;
    }

    if (this.#shouldShow()) {
      const delay = Number(this.dataset.delay) || 0;
      this.#openTimeout = setTimeout(() => this.showDialog(), delay);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#openTimeout) clearTimeout(this.#openTimeout);
    this.removeEventListener('dialog:close', this.#handleClose);
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);
  }

  /**
   * Stays on the main (fruit snacks) homepage.
   */
  chooseClassic = () => {
    this.#storeChoice('classic');
    this.closeDialog();
  };

  /**
   * Routes to the Zest Delites+ (energy gels) homepage UX.
   */
  choosePlus = () => {
    this.#storeChoice('plus');
    const url = this.dataset.plusUrl;
    if (url) {
      window.location.assign(url);
    } else {
      this.closeDialog();
    }
  };

  /**
   * Close (X) / Skip — dismisses without picking an experience.
   */
  skip = () => {
    this.closeDialog();
  };

  #shouldShow() {
    if (this.#readChoice()) return false;

    // Don't interrupt visitors who are already on the Zest+ experience.
    const plusUrl = this.dataset.plusUrl;
    if (plusUrl && window.location.pathname === new URL(plusUrl, window.location.origin).pathname) {
      return false;
    }

    if (this.dataset.homepageOnly === 'true') {
      const rootUrl = this.dataset.rootUrl || '/';
      if (window.location.pathname !== rootUrl) return false;
    }

    return true;
  }

  /**
   * Any close without an explicit choice still counts as a dismissal so the
   * visitor is not re-prompted on every page load.
   */
  #handleClose = () => {
    if (!this.#readChoice()) this.#storeChoice('dismissed');
  };

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) this.showDialog();
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) this.closeDialog();
  };

  /**
   * @returns {string | null} the stored, unexpired choice.
   */
  #readChoice() {
    let raw = null;

    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      /* localStorage unavailable — fall back to cookie */
    }

    if (!raw) {
      const match = document.cookie.match(new RegExp(`(?:^|; )${STORAGE_KEY.replace(':', '%3A')}=([^;]*)`));
      raw = match ? decodeURIComponent(match[1]) : null;
    }

    if (!raw) return null;

    try {
      const { choice, expires } = JSON.parse(raw);
      if (expires && Date.now() > expires) return null;
      return choice || null;
    } catch {
      return null;
    }
  }

  /**
   * @param {'classic' | 'plus' | 'dismissed'} choice
   */
  #storeChoice(choice) {
    const days = Number(this.dataset.repromptDays) || 0;
    const payload = JSON.stringify({
      choice,
      expires: days > 0 ? Date.now() + days * 24 * 60 * 60 * 1000 : null,
    });

    try {
      window.localStorage.setItem(STORAGE_KEY, payload);
    } catch {
      const maxAge = days > 0 ? days * 24 * 60 * 60 : 31536000;
      document.cookie = `${STORAGE_KEY.replace(':', '%3A')}=${encodeURIComponent(payload)}; path=/; max-age=${maxAge}; SameSite=Lax`;
    }
  }
}

if (!customElements.get('splash-modal-component')) {
  customElements.define('splash-modal-component', SplashModalComponent);
}
